/*
 * Rice Jam Mask landing — vanilla JS, no dependencies.
 * Loaded with `defer` by every rice-* section; the guard below makes it run once.
 */
(() => {
  if (window.RiceLanding) return;

  const configEl = document.getElementById('rice-config');
  const config = configEl ? JSON.parse(configEl.textContent) : { routes: {}, strings: {}, moneyFormat: '${{amount}}' };
  const routes = {
    root: config.routes.root || '/',
    cart: config.routes.cart || '/cart',
    cartAdd: config.routes.cartAdd || '/cart/add',
    cartChange: config.routes.cartChange || '/cart/change',
  };
  const t = (key) => config.strings[key] || '';

  /* ---------------- Helpers ---------------- */

  function formatMoney(cents, format = config.moneyFormat) {
    const value = Number(cents || 0);
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(decimals);
      const [int, dec] = fixed.split('.');
      const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? withSep + decimal + dec : withSep;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
        default: return fmt(value, 2);
      }
    });
  }

  const escapeHtml = (str) =>
    String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const storage = {
    get(key) { try { return localStorage.getItem(key); } catch { return null; } },
    set(key, value) { try { localStorage.setItem(key, value); } catch { /* private mode */ } },
  };

  async function cartRequest(url, body) {
    const isForm = body instanceof FormData;
    const res = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: isForm ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? (isForm ? body : JSON.stringify(body)) : undefined,
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || t('error'));
    return data;
  }

  /* ---------------- Urgency countdown ---------------- */

  function initUrgency(root) {
    root.querySelectorAll('[data-rice-urgency]').forEach((bar) => {
      const out = bar.querySelector('[data-rice-countdown]');
      if (!out || out.dataset.ready) return;
      out.dataset.ready = 'true';
      const ms = Number(bar.dataset.hours || 4) * 3600 * 1000;
      const key = bar.dataset.key;
      let end = Number(storage.get(key));
      if (!end || end < Date.now()) {
        end = Date.now() + ms;
        storage.set(key, String(end));
      }
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        let left = end - Date.now();
        if (left <= 0) {
          end = Date.now() + ms;
          storage.set(key, String(end));
          left = ms;
        }
        const s = Math.floor(left / 1000);
        out.textContent = `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
      };
      tick();
      setInterval(tick, 1000);
    });
  }

  /* ---------------- Hero: gallery, variants, add to cart, sticky ---------------- */

  function initHero(root) {
    root.querySelectorAll('[data-rice-hero]').forEach((hero) => {
      if (hero.dataset.ready) return;
      hero.dataset.ready = 'true';

      const track = hero.querySelector('[data-rice-track]');
      const thumbs = [...hero.querySelectorAll('[data-rice-thumb]')];
      const form = hero.querySelector('[data-rice-form]');
      const addBtn = hero.querySelector('[data-rice-add]');
      const addLabel = hero.querySelector('[data-rice-add-label]');
      const errorEl = hero.querySelector('[data-rice-error]');
      const priceEl = hero.querySelector('[data-rice-price]');
      const compareEl = hero.querySelector('[data-rice-compare]');
      const saveEl = hero.querySelector('[data-rice-save]');
      const sticky = hero.querySelector('[data-rice-sticky]');
      const stickyPrice = hero.querySelector('[data-rice-sticky-price]');
      const addText = addBtn?.dataset.addText || '';
      const soldOutText = addBtn?.dataset.soldOutText || '';
      const setLabel = (available) => {
        if (addLabel) addLabel.textContent = available ? addText : soldOutText;
      };

      // Gallery
      const setActiveThumb = (index) =>
        thumbs.forEach((th, i) => (th.dataset.active = String(i === index)));
      const goTo = (index) => {
        if (!track) return;
        track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
        setActiveThumb(index);
      };
      thumbs.forEach((th) => th.addEventListener('click', () => goTo(Number(th.dataset.riceThumb))));
      if (track) {
        let raf;
        track.addEventListener('scroll', () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => setActiveThumb(Math.round(track.scrollLeft / track.clientWidth)));
        }, { passive: true });
      }

      // Variants (pack cards)
      const onVariantChange = (input) => {
        const price = Number(input.dataset.price);
        const compare = Number(input.dataset.compare);
        const available = input.dataset.available === 'true';
        const hasSale = compare > price;
        if (priceEl) priceEl.textContent = formatMoney(price);
        if (stickyPrice) stickyPrice.textContent = formatMoney(price);
        if (compareEl) {
          compareEl.textContent = formatMoney(compare);
          compareEl.classList.toggle('hidden', !hasSale);
        }
        if (saveEl) {
          saveEl.textContent = hasSale ? t('save').replace('[amount]',formatMoney(compare - price)) : '';
          saveEl.classList.toggle('hidden', !hasSale);
        }
        if (addBtn) addBtn.disabled = !available;
        setLabel(available);
        goTo(Number(input.dataset.mediaIndex || 0));
      };
      hero.querySelectorAll('[data-rice-variant]').forEach((input) =>
        input.addEventListener('change', () => onVariantChange(input))
      );

      // Add to cart (AJAX → drawer). Without JS the form posts to /cart/add normally.
      if (form) {
        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          if (addBtn.disabled) return;
          addBtn.disabled = true;
          addBtn.setAttribute('aria-busy', 'true');
          if (addLabel) addLabel.textContent = t('adding');
          errorEl?.classList.add('hidden');
          try {
            await cartRequest(`${routes.cartAdd}.js`, new FormData(form));
            await Drawer.open({ notice: t('added'), trigger: addBtn });
          } catch (err) {
            if (errorEl) {
              errorEl.textContent = err.message;
              errorEl.classList.remove('hidden');
            }
          } finally {
            addBtn.disabled = false;
            addBtn.removeAttribute('aria-busy');
            setLabel(true);
          }
        });
      }

      // Sticky mobile CTA: visible while the main button is off-screen
      if (sticky && addBtn && 'IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          sticky.dataset.visible = String(!entry.isIntersecting);
        }).observe(addBtn);
        sticky.querySelector('[data-rice-sticky-add]')?.addEventListener('click', () => {
          if (form.requestSubmit) form.requestSubmit(addBtn);
          else addBtn.click();
        });
      }
    });
  }

  /* ---------------- Cart drawer ---------------- */

  const Drawer = (() => {
    const el = document.querySelector('[data-rice-drawer]');
    if (!el) return { open: async () => { window.location.href = routes.cart; } };

    const panel = el.querySelector('[data-rice-drawer-panel]');
    const list = el.querySelector('[data-rice-drawer-items]');
    const subtotal = el.querySelector('[data-rice-drawer-subtotal]');
    const count = el.querySelector('[data-rice-drawer-count]');
    const notice = el.querySelector('[data-rice-drawer-notice]');
    const footer = el.querySelector('[data-rice-drawer-footer]');
    let lastTrigger = null;

    // Move to <body> so no theme container (transform/overflow) can clip it.
    document.body.appendChild(el);

    function render(cart) {
      count.textContent = cart.item_count ? `(${cart.item_count})` : '';
      subtotal.textContent = formatMoney(cart.total_price);
      footer.classList.toggle('hidden', cart.item_count === 0);

      if (!cart.items.length) {
        list.innerHTML = `<li class="py-16 text-center text-rice-muted">${escapeHtml(t('empty'))}</li>`;
        return;
      }

      list.innerHTML = cart.items.map((item) => {
        const thumb = (src) => {
          const u = new URL(src, window.location.origin);
          u.searchParams.set('width', '160');
          return u.toString();
        };
        const img = item.image
          ? `<img src="${escapeHtml(thumb(item.image))}" alt="" width="80" height="80" loading="lazy" class="size-20 shrink-0 rounded-lg bg-rice-cream object-cover">`
          : '<span class="size-20 shrink-0 rounded-lg bg-rice-cream"></span>';
        const variant = item.variant_title && !item.product_has_only_default_variant
          ? `<p class="text-xs text-rice-muted">${escapeHtml(item.variant_title)}</p>` : '';
        const strike = item.original_line_price > item.final_line_price
          ? `<s class="block text-xs text-rice-muted">${formatMoney(item.original_line_price)}</s>` : '';
        return `
          <li class="flex gap-3 py-4" data-key="${escapeHtml(item.key)}">
            ${img}
            <div class="min-w-0 flex-1">
              <p class="text-sm font-semibold">${escapeHtml(item.product_title)}</p>
              ${variant}
              <div class="mt-2 flex items-center gap-3">
                <div class="inline-flex items-center rounded-full border border-rice-sand">
                  <button type="button" class="flex size-8 items-center justify-center text-lg" data-qty="${item.quantity - 1}" aria-label="${escapeHtml(t('decrease'))}">−</button>
                  <span class="w-6 text-center text-sm tabular-nums">${item.quantity}</span>
                  <button type="button" class="flex size-8 items-center justify-center text-lg" data-qty="${item.quantity + 1}" aria-label="${escapeHtml(t('increase'))}">+</button>
                </div>
                <button type="button" class="text-xs text-rice-muted underline" data-qty="0">${escapeHtml(t('remove'))}</button>
              </div>
            </div>
            <div class="text-right text-sm font-bold">${formatMoney(item.final_line_price)}${strike}</div>
          </li>`;
      }).join('');
    }

    async function refreshTheme() {
      const cart = await cartRequest(`${routes.cart}.js`);
      syncThemeCartCount(cart);
      return cart;
    }

    async function refresh() {
      const cart = await refreshTheme();
      render(cart);
      return cart;
    }

    function focusables() {
      return [...panel.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])')]
        .filter((n) => !n.disabled && n.offsetParent !== null);
    }

    function onKeydown(e) {
      if (e.key === 'Escape') return close();
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    async function open({ notice: msg = '', trigger = null } = {}) {
      // Prefer the theme's native drawer when it exposes one (Horizon: <cart-drawer-component>),
      // so shoppers keep accelerated checkouts (Shop Pay, Google Pay…) and the theme's upsells.
      const themeDrawer = document.querySelector('cart-drawer-component');
      if (themeDrawer && typeof themeDrawer.open === 'function') {
        await refreshTheme();
        if (!themeDrawer.refs?.dialog?.open) themeDrawer.open();
        return;
      }

      lastTrigger = trigger || document.activeElement;
      notice.textContent = msg;
      notice.classList.toggle('hidden', !msg);
      await refresh();
      el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('rice-drawer-open');
      document.addEventListener('keydown', onKeydown);
      panel.focus();
    }

    function close() {
      el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('rice-drawer-open');
      document.removeEventListener('keydown', onKeydown);
      lastTrigger?.focus?.();
    }

    el.querySelectorAll('[data-rice-drawer-close]').forEach((btn) => btn.addEventListener('click', close));

    list.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-qty]');
      if (!btn) return;
      const key = btn.closest('[data-key]').dataset.key;
      list.classList.add('opacity-50', 'pointer-events-none');
      try {
        const cart = await cartRequest(`${routes.cartChange}.js`, { id: key, quantity: Number(btn.dataset.qty) });
        render(cart);
        syncThemeCartCount(cart);
      } catch (err) {
        notice.textContent = err.message;
        notice.classList.remove('hidden');
      } finally {
        list.classList.remove('opacity-50', 'pointer-events-none');
      }
    });

    return { open, close, refresh };
  })();

  // Keep the theme header's cart icon in sync.
  async function syncThemeCartCount(cart) {
    // Horizon (and other Shopify "theme events" themes): cart-icon + cart drawer listen to `cart:update`.
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { resource: cart, sourceId: 'rice-landing', data: { itemCount: cart.item_count, source: 'rice-landing' } },
    }));
    document.dispatchEvent(new CustomEvent('rice:cart:updated', { detail: { cart } }));

    // Generic / Dawn-style fallbacks.
    document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble span[aria-hidden="true"]')
      .forEach((n) => (n.textContent = cart.item_count));

    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    try {
      const res = await fetch(`${routes.root}?sections=cart-icon-bubble`);
      const data = await res.json();
      if (!data['cart-icon-bubble']) return;
      const doc = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
      const inner = doc.querySelector('.shopify-section');
      if (inner) bubble.innerHTML = inner.innerHTML;
    } catch { /* theme has no such section — ignore */ }
  }

  /* ---------------- Before / after sliders ---------------- */

  function initBeforeAfter(root) {
    root.querySelectorAll('[data-rice-ba-range]').forEach((range) => {
      const wrap = range.closest('[data-rice-ba]');
      const update = () => wrap.style.setProperty('--pos', `${range.value}%`);
      range.addEventListener('input', update);
      update();
    });
  }

  /* ---------------- Reviews "show more" ---------------- */

  function initReviews(root) {
    root.querySelectorAll('[data-rice-reviews-more]').forEach((btn) => {
      btn.addEventListener('click', () => {
        btn.closest('[data-rice-reviews]')
          .querySelectorAll('[data-rice-review-hidden]')
          .forEach((n) => n.classList.remove('hidden'));
        btn.remove();
      });
    });
  }

  /* ---------------- Boot ---------------- */

  function init(root = document) {
    initUrgency(root);
    initHero(root);
    initBeforeAfter(root);
    initReviews(root);
  }

  window.RiceLanding = { init, drawer: Drawer, formatMoney };
  init();

  // Theme editor: re-init a section after it's re-rendered.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
